import React from 'react';

import type { Shm } from '../../../generated/generatedOpenAPI';

/**
 * AminoAcid Component
 *
 * Renders a single amino acid character with appropriate styling based on:
 * - Search match highlighting
 * - Region highlighting (FWR/CDR)
 * - Liability severity (ring colors)
 * - CDR vs FWR styling
 * - SHM (somatic hypermutation) annotation
 *
 * Colour is reserved for regions (CDR tints) and liabilities (severity rings).
 * SHM deliberately avoids the colour channel — it marks a mutated residue with a
 * neutral underline bar + bold glyph so it layers on top of a CDR tint or a
 * liability ring without competing with either.
 */

// All per-severity styles in one place — ring on the residue + tooltip border/text + score label
const SEVERITY_STYLES: Record<string, { ring: string; tooltip: string; score: string }> = {
  Critical: {
    ring: 'ring-red-600 bg-red-50 text-red-700',
    tooltip: 'bg-red-50 border-red-600 text-red-600',
    score: '(Score: -2.0)',
  },
  Strong: {
    ring: 'ring-orange-500 bg-orange-50 text-orange-700',
    tooltip: 'bg-orange-50 border-orange-600 text-orange-600',
    score: '(Score: -1.0)',
  },
  Mild: {
    ring: 'ring-yellow-600 bg-yellow-50 text-yellow-700',
    tooltip: 'bg-yellow-50 border-yellow-600 text-yellow-600',
    score: '(Score: -0.5)',
  },
  Weak: {
    ring: 'ring-green-600 bg-green-50 text-green-700',
    tooltip: 'bg-green-50 border-green-600 text-green-600',
    score: '(Score: 0.0)',
  },
};

const SHM_STYLES = {
  underline: 'relative z-10 font-bold scale-125 -translate-y-px after:content-[""] after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:bg-black',
  tooltip: 'bg-gray-50 border-gray-700 text-gray-800',
} as const;

const s = {
  // Base dimensions — fixed width crucial for alignment
  base: 'inline-flex items-center justify-center h-[20px] w-[9px] text-[11px] font-mono mx-[0.5px] rounded-sm select-none',
  // Variant overlays (appended to base)
  searchMatch: 'bg-yellow-400 text-black font-bold ring-2 ring-yellow-600',
  liability: 'ring-[1.5px] ring-inset font-bold',
  cdr: 'text-black font-bold',
  fwr: 'text-black',
  // Tooltip shell
  tooltipWrapper: 'relative group',
  tooltipBase:
    'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-xs font-semibold whitespace-nowrap border-2',
} as const;

interface AminoAcidProps {
  aa: string;
  isCDR: boolean;
  liabilitySeverity?: string;
  isSearchMatch?: boolean;
  shm?: Shm;
}

export const AminoAcid = React.memo<AminoAcidProps>(({ aa, isCDR, liabilitySeverity, isSearchMatch, shm }) => {
  const severity = liabilitySeverity ? (SEVERITY_STYLES[liabilitySeverity] ?? null) : null;

  // Priority: search match > liability ring > CDR bold > FWR normal
  // CDR background colour comes from the parent SequenceRegion container (bgColor prop), not here.
  let aaClassName = s.base;
  if (isSearchMatch) {
    aaClassName += ` ${s.searchMatch}`;
  } else if (severity) {
    aaClassName += ` ${s.liability} ${severity.ring}`;
  } else if (isCDR) {
    aaClassName += ` ${s.cdr}`;
  } else {
    aaClassName += ` ${s.fwr}`;
  }

  if (shm) {
    aaClassName += ` ${SHM_STYLES.underline}`;
  }

  if (severity || shm) {
    // Colour the tooltip by liability when present, otherwise the neutral SHM grey.
    const tooltipStyle = severity ? severity.tooltip : SHM_STYLES.tooltip;
    return (
      <span className={s.tooltipWrapper}>
        <span className={aaClassName}>{aa}</span>
        <span className={`${s.tooltipBase} ${tooltipStyle}`}>
          {severity && (
            <span>
              {liabilitySeverity} Liability {severity.score}
            </span>
          )}
          {severity && shm && <br />}
          {shm && (
            <span>
              SHM: {shm.ref}→{shm.residue} (pos {shm.pos})
            </span>
          )}
        </span>
      </span>
    );
  }

  return <span className={aaClassName}>{aa}</span>;
});

AminoAcid.displayName = 'AminoAcid';
